import {
    Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges,
    ViewChildren
} from '@angular/core';
import {MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {MatDatepickerInputEvent} from '@angular/material/datepicker';
import {Moment} from 'moment';
import * as moment from 'moment';
import {Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, map, switchMap, take, takeUntil} from 'rxjs/operators';
import {FilterAndData} from '../../../api/filter-list';
import {InternalPropertyMap} from '../../../basic-entity-back/basic-entity-interface/mapping-internal';
import {
    diaIso, FiltroAplicado, filtrosAplicados, filtrosDesdeValores, limpiarTexto, OpcionFiltro, OPCIONES_SINO,
    opcionesOcultas, opcionesVisibles, RangoFechas, SeccionFiltro, sinValor, ValoresPanel, valoresDesdeFiltros
} from './panel-filtros';

/** Un lado de una sección de fechas */
export type LadoFecha = keyof RangoFechas;

/** Lo que pintan los dos datepicker de una sección de fechas */
interface FechasCampo {
    desde: Moment | null;
    hasta: Moment | null;
}

/**
 * Panel de filtros al estilo de on.com: arriba los aplicados (se quitan con la ×) y «Borrar todos»; debajo una
 * sección por filtro, todas abiertas. Se aplica solo: las casillas (y Sí / No) al marcarlas, las fechas al elegirlas
 * o borrarlas, el buscador al elegir una sugerencia y los textos al dejar de escribir.
 * No navega él: emite los filtros del panel y quien lo usa (be-filtering2) los junta con el botón y la búsqueda.
 */
@Component({
    selector: 'be-panel-filtros',
    templateUrl: './panel-filtros.component.html',
    styleUrls: ['./panel-filtros.component.scss']
})
export class PanelFiltrosComponent implements OnChanges, OnDestroy {
    /** Lo que se espera desde la última tecla para aplicar un texto (no recargar con cada letra) */
    public static readonly ESPERA_TEXTO = 500;
    /** Lo que se espera desde la última tecla para pedir sugerencias en un buscador */
    public static readonly ESPERA_BUSCADOR = 300;
    /** Las letras que hacen falta para pedir sugerencias: con una saldría media base de datos */
    public static readonly MIN_LETRAS_BUSCADOR = 2;

    @Input() public secciones: SeccionFiltro[] = [];
    /** Las propiedades del modelo de la tabla, donde se buscan las de las secciones */
    @Input() public propiedades: InternalPropertyMap[] = [];
    /** Los filtros que trae la URL: el panel se pinta con ellos */
    @Input() public filtros: FilterAndData[] = [];
    /** Los filtros del panel cada vez que cambian (sin el botón ni la búsqueda general) */
    @Output() public cambio = new EventEmitter<FilterAndData[]>();

    public valores: ValoresPanel = {};
    public aplicados: FiltroAplicado[] = [];
    /**
     * Lo que pintan los datepicker, por `propiedad`. Se guarda y no se calcula en la plantilla: con un moment nuevo
     * en cada detección de cambios, el datepicker reescribiría el campo mientras se teclea
     */
    public fechas: { [propiedad: string]: FechasCampo } = {};
    /** Las sugerencias de cada buscador, por `propiedad` */
    public sugerencias: { [propiedad: string]: OpcionFiltro[] } = {};
    /** Las etiquetas conocidas de los valores del buscador (las elegidas y las ya resueltas), por `propiedad|valor` */
    private _etiquetas = new Map<string, string>();
    /** Los valores cuya etiqueta ya se ha pedido a `etiquetaDe`: se pide una sola vez */
    private _etiquetasPedidas = new Set<string>();
    /** Lo tecleado en cada buscador, de donde salen sus sugerencias */
    private _busquedas = new Map<SeccionFiltro, Subject<string>>();
    private _destruir = new Subject<void>();
    @ViewChildren(MatAutocompleteTrigger) private _disparadores: QueryList<MatAutocompleteTrigger>;
    @ViewChildren(MatAutocompleteTrigger, {read: ElementRef}) private _camposBuscador: QueryList<ElementRef<HTMLElement>>;
    private _espera: ReturnType<typeof setTimeout> | null = null;
    /** Las secciones (por `propiedad`) en las que se ha pulsado «Ver todos». Solo cambia lo que se ve, no filtra */
    private _desplegadas = new Set<string>();

    ngOnChanges(changes: SimpleChanges): void {
        // `propiedades` llega como un array nuevo en cada detección de cambios (es un getter de la tabla): eso
        // solo no es motivo para releer la URL, que borraría lo que se está escribiendo y rehará las etiquetas
        if (!changes['filtros'] && !changes['secciones']) {
            return;
        }
        const llegados = valoresDesdeFiltros(this.secciones, this.filtros || []);
        const enEspera = this._espera !== null;
        for (const seccion of this.secciones.filter(s => s.tipo === 'texto')) {
            const local = (this.valores[seccion.propiedad] as string) ?? '';
            // Mientras se espera a aplicar lo escrito, la URL trae aún el texto anterior: no pisar lo tecleado.
            // Y si lo tecleado es el mismo filtro que el de la URL (p. ej. «clinica » frente a «clinica», o un
            // texto que limpiar deja en nada), se queda lo tecleado: si no, el espacio final o lo que se esté
            // escribiendo desaparecería del campo con el eco de la navegación
            if (enEspera || limpiarTexto(seccion, local) === llegados[seccion.propiedad]) {
                llegados[seccion.propiedad] = local;
            }
        }
        this._ponerValores(llegados);
    }

    /** Para el *ngFor de «Filtros aplicados»: que no se rehagan las etiquetas que siguen igual */
    public claveAplicado(_indice: number, aplicado: FiltroAplicado): string {
        return `${aplicado.seccion.propiedad}|${aplicado.valor}`;
    }

    ngOnDestroy(): void {
        this._cancelarEspera();
        this._destruir.next();
        this._destruir.complete();
    }

    public marcado(seccion: SeccionFiltro, valor: string): boolean {
        return this._marcados(seccion).includes(valor);
    }

    private _marcados(seccion: SeccionFiltro): string[] {
        return (this.valores[seccion.propiedad] as string[]) || [];
    }

    /** Las casillas de la sección: las suyas o, en las de Sí / No, esas dos */
    private _opciones(seccion: SeccionFiltro): OpcionFiltro[] {
        return seccion.tipo === 'sino' ? OPCIONES_SINO : (seccion.opciones || []);
    }

    /** Las casillas que se pintan: las destacadas y las marcadas, o todas tras «Ver todos» */
    public visibles(seccion: SeccionFiltro): OpcionFiltro[] {
        if (seccion.tipo === 'sino') {
            return OPCIONES_SINO;
        }
        return opcionesVisibles(seccion, this._marcados(seccion), this._desplegadas.has(seccion.propiedad));
    }

    /** Para el *ngFor de las casillas: que no se rehagan al desplegar o al recibir la URL */
    public claveOpcion(_indice: number, opcion: OpcionFiltro): string {
        return opcion.valor;
    }

    /**
     * El texto del enlace bajo las casillas, o null si no hace falta: «Ver todos (N)», con N el total, si algo
     * queda oculto; «Ver menos» si está desplegada y recogerla escondería alguna
     */
    public enlaceVerTodos(seccion: SeccionFiltro): string | null {
        const marcados = this._marcados(seccion);
        if (opcionesOcultas(seccion, marcados, false) === 0) {
            return null;
        }
        return this._desplegadas.has(seccion.propiedad) ? 'Ver menos' : `Ver todos (${(seccion.opciones || []).length})`;
    }

    public alternarVerTodos(seccion: SeccionFiltro): void {
        if (!this._desplegadas.delete(seccion.propiedad)) {
            this._desplegadas.add(seccion.propiedad);
        }
    }

    public alternar(seccion: SeccionFiltro, valor: string, marcar: boolean): void {
        const actuales = ((this.valores[seccion.propiedad] as string[]) || []).filter(v => v !== valor);
        const nuevos = marcar ? [...actuales, valor] : actuales;
        // En el orden de las opciones, para que la URL no dependa del orden en que se marcaron
        const ordenados = this._opciones(seccion).map(o => o.valor).filter(v => nuevos.includes(v));
        this._ponerValores({...this.valores, [seccion.propiedad]: ordenados});
        this._emitir();
    }

    /**
     * Teclas en un campo de texto: el mat-menu se queda Inicio/Fin, las flechas y las letras para moverse por
     * sus opciones, así que se paran aquí. Escape y Tabulador sí pasan: son los que cierran el menú.
     */
    public teclaEnTexto(evento: KeyboardEvent): void {
        if (evento.key !== 'Escape' && evento.key !== 'Tab') {
            evento.stopPropagation();
        }
    }

    /**
     * Teclas en un buscador: como en un texto, pero con las sugerencias abiertas Escape solo las cierra (si no, el
     * mat-menu lo vería y se cerraría entero). Flechas e Intro ya las atiende antes el autocomplete en el propio campo.
     */
    public teclaEnBuscador(evento: KeyboardEvent, disparador: MatAutocompleteTrigger): void {
        if (evento.key === 'Escape' && disparador.panelOpen) {
            evento.stopPropagation();
            disparador.closePanel();
            return;
        }
        this.teclaEnTexto(evento);
    }

    /**
     * be-filtering2 para el click en el host del panel (si llegara al mat-menu, lo cerraría), así que no llega al
     * documento, que es donde el autocomplete escucha para cerrarse al pulsar fuera: se cierra aquí.
     */
    @HostListener('click', ['$event'])
    public clicEnElPanel(evento: MouseEvent): void {
        const campos = this._camposBuscador?.toArray() || [];
        this._disparadores?.forEach((disparador, i) => {
            const campo = campos[i]?.nativeElement.closest('.mat-form-field');
            if (disparador.panelOpen && !campo?.contains(evento.target as Node)) {
                disparador.closePanel();
            }
        });
    }

    /**
     * Una fecha elegida en el calendario, tecleada (al salir del campo o con Intro) o borrada: se aplica al momento.
     * Si lo tecleado no es una fecha, o deja «desde» después de «hasta» (el calendario ya no deja elegirlo, pero sí
     * teclearlo), no se aplica y el campo vuelve a lo que tenía.
     */
    public fechaElegida(seccion: SeccionFiltro, lado: LadoFecha, evento: MatDatepickerInputEvent<Moment>): void {
        const rango = this._rango(seccion);
        const tecleado = ((evento.targetElement as HTMLInputElement).value || '').trim();
        const dia = diaIso(evento.value);
        const otro = lado === 'desde' ? rango.hasta : rango.desde;
        const alReves = !!dia && !!otro && (lado === 'desde' ? dia > otro : dia < otro);
        if ((tecleado !== '' && dia === null) || alReves) {
            evento.target.value = this.fechas[seccion.propiedad]?.[lado] ?? null;
            return;
        }
        if (dia === rango[lado]) {
            return;
        }
        this._ponerValores({...this.valores, [seccion.propiedad]: {...rango, [lado]: dia}});
        this._emitir();
    }

    private _rango(seccion: SeccionFiltro): RangoFechas {
        return (this.valores[seccion.propiedad] as RangoFechas) || {desde: null, hasta: null};
    }

    /** Lo tecleado en un buscador: con MIN_LETRAS_BUSCADOR o más, y tras ESPERA_BUSCADOR, pide sugerencias */
    public buscar(seccion: SeccionFiltro, texto: string): void {
        this._busqueda(seccion).next(texto);
    }

    /** El flujo de sugerencias de un buscador, creado la primera vez que se teclea en él */
    private _busqueda(seccion: SeccionFiltro): Subject<string> {
        let busqueda = this._busquedas.get(seccion);
        if (!busqueda) {
            busqueda = new Subject<string>();
            this._busquedas.set(seccion, busqueda);
            busqueda.pipe(
                debounceTime(PanelFiltrosComponent.ESPERA_BUSCADOR),
                map(texto => (texto || '').trim()),
                // switchMap: una búsqueda nueva deja de escuchar la anterior, que podría llegar después
                switchMap((texto): Observable<OpcionFiltro[]> =>
                    texto.length < PanelFiltrosComponent.MIN_LETRAS_BUSCADOR || !seccion.sugerencias
                        ? of([])
                        : seccion.sugerencias(texto).pipe(catchError(() => of([])))),
                takeUntil(this._destruir)
            ).subscribe(opciones => this.sugerencias = {...this.sugerencias, [seccion.propiedad]: opciones});
        }
        return busqueda;
    }

    /** Una sugerencia elegida (clic o Intro): se añade a la sección, se aplica y el campo queda limpio */
    public elegir(seccion: SeccionFiltro, opcion: OpcionFiltro, campo: { value: string }): void {
        this._etiquetas.set(this._claveEtiqueta(seccion, opcion.valor), opcion.etiqueta);
        campo.value = '';
        this.sugerencias = {...this.sugerencias, [seccion.propiedad]: []};
        this._busqueda(seccion).next('');
        const actuales = this._marcados(seccion);
        if (actuales.includes(opcion.valor)) {
            return;
        }
        this._ponerValores({...this.valores, [seccion.propiedad]: [...actuales, opcion.valor]});
        this._emitir();
    }

    /** Para el autocomplete: al elegir no escribe nada en el campo (se vacía) */
    public sinTexto(): string {
        return '';
    }

    /** Las etiquetas de lo elegido en un buscador, que se ven también dentro de su sección */
    public elegidos(seccion: SeccionFiltro): FiltroAplicado[] {
        return this.aplicados.filter(a => a.seccion === seccion);
    }

    /** La etiqueta de un valor del buscador: la de su opción, la elegida o resuelta, o mientras tanto el valor */
    public etiqueta(seccion: SeccionFiltro, valor: string): string {
        return (seccion.opciones || []).find(o => o.valor === valor)?.etiqueta
            ?? this._etiquetas.get(this._claveEtiqueta(seccion, valor))
            ?? valor;
    }

    private _claveEtiqueta(seccion: SeccionFiltro, valor: string): string {
        return `${seccion.propiedad}|${valor}`;
    }

    /** Pide a `etiquetaDe` las etiquetas que faltan de los valores del buscador (los que llegan por la URL), una vez */
    private _resolverEtiquetas(): void {
        for (const seccion of this.secciones.filter(s => s.tipo === 'buscador' && s.etiquetaDe)) {
            for (const valor of this._marcados(seccion)) {
                const clave = this._claveEtiqueta(seccion, valor);
                if (this._etiquetas.has(clave) || this._etiquetasPedidas.has(clave)
                    || (seccion.opciones || []).some(o => o.valor === valor)) {
                    continue;
                }
                this._etiquetasPedidas.add(clave);
                seccion.etiquetaDe(valor).pipe(take(1), takeUntil(this._destruir)).subscribe({
                    next: etiqueta => {
                        this._etiquetas.set(clave, etiqueta);
                        this._ponerValores(this.valores);
                    },
                    // Sin etiqueta se queda el valor: no se reintenta
                    error: () => undefined
                });
            }
        }
    }

    public escrito(seccion: SeccionFiltro, texto: string): void {
        this._ponerValores({...this.valores, [seccion.propiedad]: texto});
        this._cancelarEspera();
        this._espera = setTimeout(() => this._emitir(), PanelFiltrosComponent.ESPERA_TEXTO);
    }

    public quitar(aplicado: FiltroAplicado): void {
        this._ponerValores(sinValor(this.valores, aplicado));
        this._emitir();
    }

    public borrarTodos(): void {
        this._ponerValores(valoresDesdeFiltros(this.secciones, []));
        this._emitir();
    }

    private _ponerValores(valores: ValoresPanel): void {
        this.valores = valores;
        this.aplicados = filtrosAplicados(this.secciones, valores,
            (seccion, valor) => this._etiquetas.get(this._claveEtiqueta(seccion, valor)));
        this._ponerFechas();
        this._resolverEtiquetas();
    }

    /** Los moment de los datepicker; uno que no cambia de día se queda el mismo objeto (el campo no se reescribe) */
    private _ponerFechas(): void {
        const fechas: { [propiedad: string]: FechasCampo } = {};
        for (const seccion of this.secciones.filter(s => s.tipo === 'fechas')) {
            const rango = this._rango(seccion);
            const antes = this.fechas[seccion.propiedad];
            const momento = (lado: LadoFecha): Moment | null => {
                const dia = rango[lado];
                if (!dia) {
                    return null;
                }
                return antes?.[lado] && diaIso(antes[lado]) === dia ? antes[lado] : moment(dia, 'YYYY-MM-DD');
            };
            fechas[seccion.propiedad] = {desde: momento('desde'), hasta: momento('hasta')};
        }
        this.fechas = fechas;
    }

    private _emitir(): void {
        // Lo que se estuviera escribiendo va ya en esta emisión
        this._cancelarEspera();
        this.cambio.emit(filtrosDesdeValores(this.secciones, this.valores, this.propiedades));
    }

    private _cancelarEspera(): void {
        if (this._espera !== null) {
            clearTimeout(this._espera);
            this._espera = null;
        }
    }
}
